import { memo, isValidElement, type AnchorHTMLAttributes, type ImgHTMLAttributes } from 'react'
import type { Components } from 'react-markdown'
import { CodeBlock } from './MessageCodeBlock'

const isCodeBlockWrapper = (child: React.ReactNode): boolean => {
  if (!isValidElement(child)) return false

  return child.key?.startsWith('code-') ?? false
}

interface TCodeProps {
  inline?: boolean
  className?: string
  children?: React.ReactNode
}

const CodeComponent = memo(({ className, children }: TCodeProps) => {
  const match = /language-(\w+)/.exec(className ?? '')
  const lang = match && match[1]
  const isMath = lang === 'math'
  const isSingleLine = typeof children === 'string' && children.split('\n').length === 1

  if (isMath) {
    return <>{children}</>
  }

  if (isSingleLine || !match) {
    const handleDoubleClick = (e: React.MouseEvent<HTMLElement>) => {
      const selection = window.getSelection()
      if (selection) {
        const range = document.createRange()
        range.selectNodeContents(e.currentTarget)
        selection.removeAllRanges()
        selection.addRange(range)
      }
    }

    return (
      <code onDoubleClick={handleDoubleClick} className={className || 'mp-agent-inline-code'}>
        {children}
      </code>
    )
  }

  return <CodeBlock lang={lang || undefined} codeChildren={children} allowExecution={false} />
})

const PreComponent = memo(({ children, ...props }: { children?: React.ReactNode; [key: string]: any }) => {
  const childrenArray = Array.isArray(children) ? children : [children]

  const codeBlockWrapper = childrenArray.find(isCodeBlockWrapper)

  // If the child is our custom CodeBlock, render it directly without the extra <pre>
  if (codeBlockWrapper) {
    return <>{codeBlockWrapper}</>
  }

  return (
    <pre className='mp-agent-markdown-pre' {...props}>
      {children}
    </pre>
  )
})

const ParagraphComponent = memo(({ children, ...props }: { children?: React.ReactNode; [key: string]: any }) => {
  return (
    <div className='mp-agent-markdown-p' {...props}>
      {children}
    </div>
  )
})

const AnchorComponent = memo(
  ({
    href,
    children,

    ...props
  }: {
    href?: string
    children?: React.ReactNode

    [key: string]: any
  }) => {
    const isExternal = href?.startsWith('http')
    const target = isExternal ? '_blank' : undefined
    const rel = isExternal ? 'noopener noreferrer' : undefined

    return (
      <a
        href={href || undefined}
        target={target}
        rel={rel}
        className='mp-agent-markdown-link'
        {...(props as AnchorHTMLAttributes<HTMLAnchorElement>)}
      >
        {children}
      </a>
    )
  }
)

const ImageComponent = memo(
  ({
    src,
    alt,
    title,
    className,
    style,
    ...props
  }: {
    src?: string
    alt?: string
    title?: string
    className?: string
    style?: React.CSSProperties
    [key: string]: any
  }) => {
    if (!src) return null

    return (
      <img
        src={src || undefined}
        alt={alt}
        title={title}
        style={style}
        loading='lazy'
        className={className || 'mp-agent-markdown-img'}
        {...(props as ImgHTMLAttributes<HTMLImageElement>)}
      />
    )
  }
)

export const markdownComponents: Components = {
  code: CodeComponent as Components['code'],
  pre: PreComponent as Components['pre'],
  p: ParagraphComponent as Components['p'],
  a: AnchorComponent as Components['a'],
  img: ImageComponent as Components['img']
}
